<template>
  <Teleport to="body">
    <div ref="modal" class="modal boq-item-images-modal" tabindex="-1" aria-labelledby="boq-item-images-title">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <span class="boq-images-heading-icon" aria-hidden="true"><i class="fa-regular fa-images"></i></span>
            <div class="boq-images-heading">
              <h5 id="boq-item-images-title" class="modal-title">{{ $t('Line item images') }}</h5>
              <div class="boq-images-context"><span>{{ item.label }}</span><span>{{ item.name }}</span></div>
            </div>
            <button type="button" class="btn-close" :disabled="busy" :aria-label="$t('Close')" @click="close" />
          </div>
          <div class="modal-body" :aria-busy="busy || loading">
            <div v-if="error" class="alert alert-danger" role="alert">{{ error }}</div>
            <div v-if="notice" class="alert alert-success" role="status">{{ notice }}</div>
            <div class="boq-images-toolbar">
              <div class="boq-images-section-title">{{ $t('Attached images') }}<span class="boq-images-count">{{ images.length }}</span></div>
              <button type="button" class="boq-images-button boq-images-button--quiet" :disabled="busy || loading" @click="loadImages">
                <i class="fa-solid fa-rotate-right me-1" aria-hidden="true" />{{ $t('Refresh images') }}
              </button>
            </div>
            <div v-if="loading" class="text-center py-4" role="status">
              <span class="spinner-border spinner-border-sm me-2" aria-hidden="true" />{{ $t('Loading images…') }}
            </div>
            <div v-else-if="!loaded" class="text-center py-4">
              <p>{{ $t('Images could not be loaded.') }}</p>
              <button type="button" class="btn btn-outline-primary" @click="loadImages">{{ $t('Try again') }}</button>
            </div>
            <div v-else-if="viewingImage">
              <div class="boq-images-viewer-toolbar">
                <button type="button" class="boq-images-button" @click="backToImages"><i class="fa-solid fa-arrow-left" aria-hidden="true"></i>{{ $t('Back to images') }}</button>
                <button type="button" class="boq-images-button" :aria-pressed="imageZoomed" @click="imageZoomed = !imageZoomed">{{ imageZoomed ? $t('Fit to width') : $t('Actual size') }}</button>
                <a v-if="viewerLoaded" :href="viewerImageUrl" target="_blank" rel="noopener noreferrer" class="boq-images-button">{{ $t('Open original') }}<i class="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i></a>
              </div>
              <p class="small fw-bold text-break">{{ imageName(viewingImage) }}</p>
              <p v-if="viewerFailed" class="alert alert-warning">{{ $t('Image could not be displayed. Refresh images and try again.') }}</p>
              <div v-else class="boq-image-viewer" tabindex="0" :aria-label="$t('Image viewer')">
                <p v-if="!viewerLoaded" class="small text-muted p-3" role="status">{{ $t('Loading image…') }}</p>
                <BoqItemImage :key="viewingImage.url" :resolvedSrc="previewSources[viewingImage._id] || ''" :boqId="boqId" :itemId="item._id" :image="viewingImage" :imageContextType="imageContextType" :alt="imageName(viewingImage)" :class="{ 'boq-image-actual-size': imageZoomed }" @load="viewerImageLoaded" @error="viewerFailed = true" />
              </div>
              <p class="small text-muted mt-2 mb-0">{{ $t('Scroll to see the full image. Use Actual size to read small details.') }}</p>
            </div>
            <div v-if="loaded" v-show="!loading && !viewingImage">
              <div v-if="!images.length" class="boq-images-empty text-center p-4 mb-3">
                <i class="fa-regular fa-images fs-3 mb-2 text-muted" aria-hidden="true" />
                <p class="mb-0">{{ $t('No images attached to this line item yet.') }}</p>
              </div>
              <div v-else class="boq-images-grid">
                <article v-for="image in images" :key="image._id" class="boq-image-card">
                    <button type="button" class="boq-image-preview-button" :disabled="busy" :aria-label="$t('View image') + ': ' + imageName(image)" @click="viewImage(image)">
                      <BoqItemImage v-if="!failedPreviews[image._id]" class="boq-image-preview card-img-top" :boqId="boqId" :itemId="item._id" :image="image" :imageContextType="imageContextType" :alt="imageName(image)" @load="previewImageLoaded(image, $event)" @error="failedPreviews[image._id] = true" />
                      <span v-if="!loadedPreviews[image._id] || failedPreviews[image._id]" class="boq-image-preview-status small text-muted" role="status">
                        {{ failedPreviews[image._id] ? $t('Preview unavailable. Refresh images to try again.') : $t('Loading image…') }}
                      </span>
                      <span v-else class="boq-image-zoom" aria-hidden="true"><i class="fa-solid fa-expand"></i></span>
                    </button>
                    <div class="boq-image-card-body">
                      <p class="boq-image-name" :title="imageName(image)">{{ imageName(image) }}</p>
                      <p class="boq-image-meta"><span>{{ imageFormat(image) }}</span><span>{{ formatSize(image.size) }}</span></p>
                      <div v-if="canManage && removeCandidate === image._id" class="boq-image-remove-confirm">
                          <p>{{ $t('Remove this image?') }}</p>
                          <div class="boq-image-card-actions">
                          <button type="button" class="boq-images-button boq-images-button--danger" :disabled="busy" @click="removeImage(image)">
                            {{ removingId === image._id ? $t('Removing…') : $t('Remove') }}
                          </button>
                          <button type="button" class="boq-images-button" :disabled="busy" @click="removeCandidate = null">{{ $t('Cancel') }}</button>
                          </div>
                      </div>
                      <div v-else class="boq-image-card-actions">
                        <button type="button" class="boq-images-button boq-images-button--view" :disabled="busy" @click="viewImage(image)"><i class="fa-solid fa-expand" aria-hidden="true"></i>{{ $t('View image') }}</button>
                        <button v-if="canManage" type="button" class="boq-images-button boq-images-button--remove" :disabled="busy || loading" :title="$t('Remove image')" :aria-label="$t('Remove image') + ': ' + imageName(image)" @click="removeCandidate = image._id"><i class="fa-regular fa-trash-can" aria-hidden="true"></i></button>
                      </div>
                    </div>
                </article>
              </div>
              <div v-if="canManage" class="boq-images-upload-section">
                <div class="boq-images-upload-zone" :class="{'boq-images-upload-zone--disabled': busy || loading || uploadNeedsRefresh || atImageLimit}">
                  <span class="boq-images-upload-icon" aria-hidden="true"><i class="fa-solid fa-arrow-up-from-bracket"></i></span>
                  <div class="boq-images-upload-copy" aria-hidden="true"><strong>{{ $t('Add images') }}</strong><span>{{ $t('Select multiple files. Uploads start automatically.') }}</span></div>
                  <span class="boq-images-browse" aria-hidden="true">{{ $t('Choose images') }}<i class="fa-solid fa-plus"></i></span>
                  <input id="boq-item-image-file" ref="fileInput" type="file" class="boq-images-file-input" accept="image/png,image/jpeg,image/gif,image/webp" multiple :disabled="busy || loading || uploadNeedsRefresh || atImageLimit" :aria-label="$t('Choose images to upload')" aria-describedby="boq-item-image-help" @change="selectFiles" />
                </div>
                <p id="boq-item-image-help" class="boq-images-upload-help"><span class="visually-hidden">{{ $t('Select multiple files. Uploads start automatically.') }} </span>{{ $t('PNG, JPEG, GIF or WebP · Maximum 3 images · Up to 2 MB per image') }}</p>
                <p v-if="uploadNeedsRefresh" class="small text-danger mt-2 mb-0">{{ $t('Refresh images to check what was saved before selecting files again.') }}</p>
              </div>
              <p v-else class="small text-muted border-top pt-3 mb-0">{{ $t('Images are available to view. Editing is disabled for this line item.') }}</p>
              <div v-if="uploadQueue.length" class="boq-images-upload-results" aria-live="polite">
                <div v-if="uploading" class="mb-3" role="status">
                  <p class="small mb-2"><span class="spinner-border spinner-border-sm me-2" aria-hidden="true" />{{ $t('Uploading images…') }} {{ completedUploads }} / {{ uploadQueue.length }} · {{ overallUploadPercent }}%</p>
                  <div class="progress boq-upload-progress">
                    <div class="progress-bar" role="progressbar" :style="{ width: overallUploadPercent + '%' }" :aria-valuenow="overallUploadPercent" aria-valuemin="0" aria-valuemax="100" :aria-label="$t('Image upload progress')" />
                  </div>
                  <p class="small text-muted mt-2 mb-0">{{ $t('Please keep this window open.') }}</p>
                </div>
                <ul class="list-unstyled small mb-0">
                  <li v-for="(entry, index) in uploadQueue" :key="index" class="border-top py-2">
                    <div class="d-flex justify-content-between align-items-start gap-3">
                      <span class="text-break">{{ entry.name }}</span>
                      <span class="text-nowrap" :class="entry.status === 'uploaded' ? 'text-success' : ['failed', 'skipped', 'unconfirmed'].includes(entry.status) ? 'text-danger' : 'text-muted'">{{ uploadStatus(entry) }}</span>
                    </div>
                    <p v-if="entry.error" class="text-danger mt-1 mb-0">{{ entry.error }}</p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <span v-if="canManage" class="boq-images-save-note"><i class="fa-regular fa-circle-check" aria-hidden="true"></i>{{ $t('Changes are saved automatically') }}</span>
            <button type="button" class="boq-images-button boq-images-button--close" :disabled="busy" @click="close">{{ $t('Done') }}</button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { Modal } from 'bootstrap'
import { GetBoqItemImages, UploadBoqItemImage, DeleteBoqItemImage } from '@/actions/boqImageActions.js'
import BoqItemImage from '@/components/boq/BoqItemImage.vue'
import { OptimizeBoqImageFile } from '@/helpers/boqImageCompression.js'
import { BOQ_IMAGE_TYPES, MAX_BOQ_IMAGE_BYTES, MAX_BOQ_IMAGES_PER_ITEM } from '@/helpers/boqImageLimits.js'

const MAX_IMAGE_BYTES = MAX_BOQ_IMAGE_BYTES
const MAX_IMAGES_PER_ITEM = MAX_BOQ_IMAGES_PER_ITEM
const IMAGE_TYPES = BOQ_IMAGE_TYPES

export default {
  name: 'BoqItemImagesModal',
  components: { BoqItemImage },
  props: {
    boqId: { type: String, required: true },
    imageContextType: { type: String, default: 'boq' },
    item: { type: Object, required: true },
    allowEdit: { type: Boolean, default: false }
  },
  emits: ['close', 'images-changed'],
  data() {
    return {
      images: [], loading: false, loaded: false, serverCanEdit: false,
      uploading: false, uploadPercent: 0, uploadQueue: [], uploadNeedsRefresh: false,
      removingId: null, removeCandidate: null, failedPreviews: {}, loadedPreviews: {}, previewSources: {}, error: '', notice: '',
      viewingImage: null, imageZoomed: false, viewerLoaded: false, viewerFailed: false, viewerImageUrl: ''
    }
  },
  computed: {
    busy() { return this.uploading || Boolean(this.removingId) },
    canManage() { return this.allowEdit && this.serverCanEdit && this.loaded },
    atImageLimit() { return this.images.length >= MAX_IMAGES_PER_ITEM },
    completedUploads() { return this.uploadQueue.filter(entry => !['pending', 'preparing', 'uploading'].includes(entry.status)).length },
    overallUploadPercent() {
      if (!this.uploadQueue.length) return 0
      const activeProgress = this.uploading ? this.uploadPercent / 100 : 0
      return Math.min(100, Math.floor((this.completedUploads + activeProgress) * 100 / this.uploadQueue.length))
    }
  },
  mounted() {
    this.modalController = new Modal(this.$refs.modal)
    this.$refs.modal.addEventListener('hide.bs.modal', this.beforeHide)
    this.$refs.modal.addEventListener('hidden.bs.modal', this.afterHide)
    this.modalController.show()
    this.loadImages()
  },
  beforeUnmount() {
    this.$refs.modal.removeEventListener('hide.bs.modal', this.beforeHide)
    this.$refs.modal.removeEventListener('hidden.bs.modal', this.afterHide)
    this.modalController.hide()
    this.modalController.dispose()
  },
  methods: {
    beforeHide(event) { if (this.busy) event.preventDefault() },
    afterHide() { this.$emit('close') },
    close() { if (!this.busy) this.modalController.hide() },
    imageName(image) { return image.originalFilename || image.filename || this.$t('Image') },
    imageFormat(image) { return (image.contentType || '').split('/')[1]?.toUpperCase() || this.$t('Image') },
    viewImage(image) {
      if (this.busy) return
      this.viewingImage = image
      this.imageZoomed = false
      this.viewerLoaded = false
      this.viewerFailed = false
      this.viewerImageUrl = ''
    },
    previewImageLoaded(image, event) {
      if (event?.displayUrl && event.url === image.url) this.previewSources[image._id] = event.displayUrl
      this.loadedPreviews[image._id] = true
      this.failedPreviews[image._id] = false
    },
    backToImages() {
      this.viewingImage = null
      this.viewerImageUrl = ''
    },
    viewerImageLoaded(event) {
      if (event.url !== this.viewingImage?.url) return
      this.viewerLoaded = true
      this.viewerFailed = false
      this.viewerImageUrl = event.displayUrl
      this.previewImageLoaded(this.viewingImage)
    },
    formatSize(size) {
      return size >= 1024 * 1024 ? `${(size / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.ceil((size || 0) / 1024))} KB`
    },
    errorMessage(error, action) {
      const status = error.response?.status
      if (status === 401) return this.$t('Your session has expired. Sign in again and retry.')
      if (status === 403) return this.$t('You do not have permission to change images for this line item, or the BOQ is locked.')
      if (status === 404) return this.$t('The line item or image is no longer available. Refresh the BOQ page and try again.')
      if (error.response?.data?.code === 'IMAGE_LIMIT_REACHED') return this.$t('A maximum of 3 images is allowed per line item.')
      if (status === 409) return this.$t('This line item changed while you were working. Refresh images and try again.')
      if (status === 413) return this.$t('The image is too large. Choose a file of 2 MB or less.')
      if (status === 400 || status === 415 || status === 422) return this.$t('The image could not be accepted. Choose a valid PNG, JPEG, GIF or WebP image of 2 MB or less.')
      if (action === 'upload') return this.$t('The upload could not be confirmed. Refresh images before trying again.')
      if (action === 'remove') return this.$t('The image could not be removed. Refresh images and try again.')
      return this.$t('Unable to load images. Check your connection and try again.')
    },
    async loadImages() {
      if (this.busy || this.loading) return
      this.loading = true
      this.error = ''
      this.notice = ''
      this.removeCandidate = null
      this.viewingImage = null
      try {
        const result = await GetBoqItemImages(this.boqId, this.item._id, this.imageContextType)
        this.images = result.images
        this.serverCanEdit = result.canEdit === true
        this.uploadNeedsRefresh = false
        this.failedPreviews = {}
        this.loadedPreviews = {}
        this.previewSources = {}
        this.loaded = true
        this.notifyImagesChanged()
      } catch (error) {
        this.loaded = false
        this.serverCanEdit = false
        this.error = this.errorMessage(error, 'load')
      } finally {
        this.loading = false
      }
    },
    notifyImagesChanged() {
      this.$emit('images-changed', {
        itemId: this.item._id,
        image: this.images[0] || null,
        count: this.images.length
      })
    },
    uploadStatus(entry) {
      if (entry.status === 'uploaded') return this.$t('Uploaded')
      if (entry.status === 'failed') return this.$t('Failed')
      if (entry.status === 'unconfirmed') return this.$t('Not confirmed')
      if (entry.status === 'skipped') return this.$t('Not uploaded')
      if (entry.status === 'pending') return this.$t('Waiting')
      if (entry.status === 'preparing') return this.$t('Optimizing image before upload')
      return this.uploadPercent < 100 ? `${this.$t('Uploading…')} ${this.uploadPercent}%` : this.$t('Saving image…')
    },
    async selectFiles(event) {
      const files = Array.from(event.target.files || [])
      event.target.value = ''
      if (!files.length || !this.canManage || this.busy || this.loading || this.uploadNeedsRefresh) return
      this.error = ''
      this.notice = ''
      this.removeCandidate = null
      const remainingSlots = Math.max(0, MAX_IMAGES_PER_ITEM - this.images.length)
      this.uploadQueue = files.map((file, index) => {
        let error = ''
        if (index >= remainingSlots) error = this.$t('A maximum of 3 images is allowed per line item.')
        else if (!IMAGE_TYPES.includes(file.type)) error = this.$t('Choose a PNG, JPEG, GIF or WebP image.')
        else if (!file.size) error = this.$t('Choose a non-empty image of 2 MB or less.')
        return { name: file.name, status: error ? 'failed' : 'pending', error }
      })
      this.uploading = true
      this.uploadPercent = 0
      try {
        let stopMessage = ''
        for (let index = 0; index < files.length; index++) {
          const entry = this.uploadQueue[index]
          if (entry.status === 'failed') continue
          if (stopMessage || !this.canManage) {
            entry.status = 'skipped'
            entry.error = stopMessage || this.$t('Editing is disabled for this line item. Refresh images before trying again.')
            continue
          }
          entry.status = 'preparing'
          let uploadFile
          try {
            uploadFile = await OptimizeBoqImageFile(files[index])
            if (!uploadFile.size || uploadFile.size > MAX_IMAGE_BYTES) {
              entry.status = 'failed'
              entry.error = this.$t('Image exceeds limit after optimization')
              continue
            }
          } catch {
            entry.status = 'failed'
            entry.error = this.$t('Image optimization failed')
            continue
          }
          entry.status = 'uploading'
          this.uploadPercent = 0
          try {
            const result = await UploadBoqItemImage(this.boqId, this.item._id, uploadFile, event => {
              if (event.total) this.uploadPercent = Math.min(100, Math.round(event.loaded * 100 / event.total))
            }, this.imageContextType)
            if (!result.image?._id) throw new Error('Upload response did not confirm an image')
            this.images.push(result.image)
            entry.status = 'uploaded'
            this.serverCanEdit = result.canEdit === true
            this.notifyImagesChanged()
          } catch (error) {
            const status = error.response?.status
            entry.status = [400, 401, 403, 404, 409, 413, 415, 422].includes(status) ? 'failed' : 'unconfirmed'
            entry.error = this.errorMessage(error, 'upload')
            if (status === 401 || status === 403) this.serverCanEdit = false
            if ([401, 403, 404, 409].includes(status)) stopMessage = entry.error
            if (![400, 413, 415, 422].includes(status)) this.uploadNeedsRefresh = true
          } finally {
            this.uploadPercent = 0
          }
        }
        const uploaded = this.uploadQueue.filter(entry => entry.status === 'uploaded').length
        if (uploaded) this.notice = `${uploaded} ${this.$t('images uploaded and saved to this line item.')}`
        if (uploaded < files.length) this.error = this.$t('Some uploads need attention. See the details below.')
      } finally {
        this.uploading = false
      }
    },
    async removeImage(image) {
      if (!this.canManage || this.busy || this.loading) return
      this.removingId = image._id
      this.error = ''
      this.notice = ''
      try {
        await DeleteBoqItemImage(this.boqId, this.item._id, image._id, this.imageContextType)
        this.images = this.images.filter(existing => existing._id !== image._id)
        this.notifyImagesChanged()
        this.removeCandidate = null
        this.notice = this.$t('Image removed.')
      } catch (error) {
        if (error.response?.status === 403) this.serverCanEdit = false
        this.error = this.errorMessage(error, 'remove')
      } finally {
        this.removingId = null
      }
    }
  }
}
</script>

<style scoped>
.boq-item-images-modal { --images-accent: var(--lens-accent-dark, #ca5015); --images-border: #e5e3df; color: #242a32; font-family: 'Albert Sans', Arial, sans-serif; }
.boq-item-images-modal button,
.boq-item-images-modal input,
.boq-item-images-modal .modal-header,
.boq-item-images-modal .modal-footer,
.boq-item-images-modal .progress-bar { border-radius: 0; }
.boq-item-images-modal .modal-dialog { max-width: 880px; }
.boq-item-images-modal .modal-content { overflow: hidden; border: 1px solid rgba(45, 39, 33, 0.08); border-radius: 0; background: #fff; box-shadow: 0 24px 80px rgba(24, 27, 31, 0.22); }
.boq-item-images-modal .modal-header { gap: 14px; padding: 22px 24px; border-bottom: 1px solid var(--images-border); }
.boq-images-heading-icon { display: grid; place-items: center; flex: 0 0 44px; height: 44px; border: 1px solid #f2dccd; border-radius: 0; background: #fff3eb; color: var(--images-accent); font-size: 21px; }
.boq-images-heading { min-width: 0; }
.boq-item-images-modal .modal-title { color: #242a32; font-family: inherit; font-size: 22px; font-weight: 700; line-height: 1.25; letter-spacing: -0.4px; }
.boq-images-context { display: flex; align-items: baseline; gap: 9px; margin-top: 6px; color: #65717d; font-size: 13px; }
.boq-images-context span:first-child { flex: 0 0 auto; padding: 0 6px; border-radius: 0; background: #f0f1f3; color: #525c68; font-weight: 600; }
.boq-images-context span:last-child { overflow-wrap: anywhere; }
.boq-item-images-modal .btn-close { flex: 0 0 auto; margin-left: auto; padding: 10px; opacity: 0.55; }
.boq-item-images-modal .modal-body { padding: 20px 24px 24px; background: #fafaf9; }
.boq-images-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.boq-images-section-title { display: flex; align-items: center; gap: 9px; font-size: 14px; font-weight: 650; }
.boq-images-count { color: #646d76; font-size: 12px; font-weight: 500; }
.boq-images-button { display: inline-flex; justify-content: center; align-items: center; gap: 7px; min-height: 36px; padding: 7px 13px; border: 1px solid #dce0e5; border-radius: 0; background: #fff; color: #46525f; font: 600 13px/1.4 'Albert Sans', Arial, sans-serif; text-transform: none; text-decoration: none; transition: background-color 0.15s, border-color 0.15s; }
.boq-images-button:hover:not(:disabled) { border-color: #b5bec7; background: #f3f5f7; color: #273849; }
.boq-images-button:focus-visible, .boq-image-preview-button:focus-visible, .boq-item-images-modal .btn-close:focus-visible { outline: 3px solid var(--images-accent); outline-offset: 3px; box-shadow: none; }
.boq-images-button:disabled, .boq-image-preview-button:disabled { opacity: 0.5; cursor: default; }
.boq-images-button--quiet { min-height: 32px; padding: 5px 0 5px 9px; border-color: transparent; background: transparent; color: #65717d; font-weight: 500; }
.boq-images-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
.boq-image-card { display: flex; flex-direction: column; min-width: 0; overflow: hidden; border: 1px solid var(--images-border); border-radius: 0; background: #fff; box-shadow: 0 2px 5px rgba(32, 35, 40, 0.025); }
.boq-image-preview-button { display: block; position: relative; flex: 0 0 auto; width: 100%; height: 190px; padding: 12px; overflow: hidden; border: 0; border-bottom: 1px solid #eee; background: #f0f1f3; cursor: zoom-in; }
.boq-image-preview { display: block; width: 100%; height: 100%; object-fit: cover; object-position: top; border: 1px solid #e6e7e9; border-radius: 0; background: #fff; }
.boq-image-preview-status { position: absolute; inset: 0; display: flex; justify-content: center; align-items: center; padding: 24px; background: #f0f1f3; }
.boq-image-zoom { display: grid; place-items: center; position: absolute; right: 22px; bottom: 22px; width: 28px; height: 28px; border: 1px solid #e1e4e7; border-radius: 0; background: rgba(255, 255, 255, 0.95); color: #667480; font-size: 12px; transition: color 0.15s; }
.boq-image-preview-button:hover .boq-image-zoom { color: var(--images-accent); }
.boq-image-card-body { display: flex; flex: 1; flex-direction: column; padding: 15px 16px 16px; }
.boq-image-name { display: -webkit-box; overflow: hidden; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow-wrap: anywhere; margin: 0; color: #29313a; font-size: 14px; font-weight: 650; line-height: 1.4; }
.boq-image-meta { display: flex; align-items: center; gap: 8px; margin: 5px 0 14px; color: #65717d; font-size: 12px; }
.boq-image-meta span:first-child { font-size: 10px; font-weight: 700; letter-spacing: 0.6px; }
.boq-image-meta span + span::before { content: '·'; margin-right: 8px; }
.boq-image-card-actions { display: flex; align-items: center; gap: 8px; margin-top: auto; }
.boq-images-button--view { flex: 1; border-color: #f0d9ca; background: #fff6f0; color: #a64200; }
.boq-images-button--view:hover:not(:disabled) { border-color: #e7bda4; background: #ffede1; color: #a8420e; }
.boq-images-button--remove { flex: 0 0 36px; width: 36px; padding: 7px; color: #7f8993; }
.boq-images-button--remove:hover:not(:disabled) { border-color: #eeb8b8; background: #fff2f2; color: #c33d3d; }
.boq-image-remove-confirm { margin-top: auto; padding-top: 0; }
.boq-image-remove-confirm p { margin: 0 0 8px; color: #b63838; font-size: 12px; font-weight: 600; }
.boq-images-button--danger { border-color: #c73f3f; background: #c73f3f; color: #fff; }
.boq-images-button--danger:hover:not(:disabled) { border-color: #ac2f2f; background: #ac2f2f; color: #fff; }
.boq-images-upload-section { margin-top: 20px; }
.boq-images-upload-zone { position: relative; display: flex; align-items: center; gap: 13px; padding: 18px; border: 1px dashed #d9bca8; border-radius: 0; background: #fff; transition: background-color 0.15s, border-color 0.15s; }
.boq-images-upload-zone:hover:not(.boq-images-upload-zone--disabled), .boq-images-upload-zone:focus-within { border-color: var(--images-accent); background: #fff9f4; }
.boq-images-upload-zone:focus-within { outline: 3px solid var(--images-accent); outline-offset: 3px; }
.boq-images-upload-icon { display: grid; flex: 0 0 38px; height: 38px; place-items: center; border-radius: 0; background: #fff0e5; color: var(--images-accent); font-size: 16px; }
.boq-images-upload-copy { display: flex; flex: 1; flex-direction: column; gap: 4px; min-width: 0; }
.boq-images-upload-copy strong { color: #353d46; font-size: 14px; font-weight: 650; }
.boq-images-upload-copy span { color: #65717d; font-size: 12px; line-height: 1.5; }
.boq-images-browse { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 10px; min-height: 36px; padding: 8px 13px; border-radius: 0; background: var(--images-accent); color: #fff; font-size: 12px; font-weight: 650; }
.boq-images-file-input { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
.boq-images-upload-zone--disabled { opacity: 0.55; }
.boq-images-file-input:disabled { cursor: default; }
.boq-images-upload-help { margin: 9px 0 0; color: #65717d; font-size: 11px; text-align: center; }
.boq-images-upload-results { margin-top: 18px; padding: 15px; border: 1px solid var(--images-border); border-radius: 0; background: #fff; }
.boq-images-empty { margin-bottom: 0 !important; border: 1px solid var(--images-border); border-radius: 0; background: #fff; color: #7b838d; }
.boq-images-viewer-toolbar { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px; }
.boq-image-viewer { max-height: 56vh; overflow: auto; border: 1px solid var(--images-border); border-radius: 0; background: #eef0f2; }
.boq-image-viewer img { display: block; width: 100%; height: auto; }
.boq-image-viewer img.boq-image-actual-size { width: auto; max-width: none; }
.boq-upload-progress { height: 6px; border-radius: 0; background: #f2e6dc; }
.boq-upload-progress .progress-bar { background: var(--images-accent); }
.boq-item-images-modal .modal-footer { justify-content: space-between; gap: 12px; padding: 15px 24px; border-top: 1px solid var(--images-border); background: #fff; }
.boq-images-save-note { display: flex; align-items: center; gap: 7px; margin: 0; color: #65717d; font-size: 11px; }
.boq-images-save-note i { color: #5f9d83; }
.boq-images-button--close { min-width: 80px; margin: 0 0 0 auto; background: #283440; border-color: #283440; color: #fff; }
.boq-images-button--close:hover:not(:disabled) { background: #172330; border-color: #172330; color: #fff; }
.boq-item-images-modal .alert { padding: 12px 14px; border-radius: 0; font-size: 13px; line-height: 1.5; }
.boq-item-images-modal .alert.alert-success {
  border: 1px solid #bddbc8;
  background: #eff8f3 !important;
  color: #14532d !important;
  font-size: 14px;
  font-weight: 600;
}
.boq-item-images-modal .alert-danger {
  border: 1px solid #c91f2c;
  background: #dc2635;
  color: #ffffff !important;
  font-weight: 600;
}
@media (max-width: 575px) {
  .boq-item-images-modal .modal-dialog { margin: 12px; }
  .boq-item-images-modal .modal-header { gap: 10px; padding: 18px; }
  .boq-item-images-modal .modal-title { font-size: 20px; }
  .boq-item-images-modal .modal-body { padding: 16px 18px 20px; }
  .boq-images-grid { grid-template-columns: minmax(0, 1fr); gap: 14px; }
  .boq-image-preview-button { height: 185px; }
  .boq-images-upload-zone { display: grid; grid-template-columns: 38px minmax(0, 1fr); padding: 15px; gap: 12px; }
  .boq-images-browse { grid-column: 2; justify-self: start; }
  .boq-item-images-modal .modal-footer { padding: 14px 18px; }
  .boq-images-save-note { max-width: 65%; }
}
</style>
